// Genera en el navegador la imagen 1080x1920 para historias de Instagram.
// Solo incluye el nombre de pila si la usuaria lo permite. Nunca email ni la URL de la app.

export const MILESTONES = [7, 14, 28] as const;
export type Milestone = (typeof MILESTONES)[number];

export const MILESTONE_COPY: Record<Milestone, string> = {
  7: "Tu primera semana ya es tuya.",
  14: "Llegaste a la mitad del Reto.",
  28: "Terminaste el Reto de 28 días.",
};

export interface AchievementStats {
  completedDays: number[];
  totalMinutes: number;
  streak: number;
}

const C = {
  coral: "#C9485B",
  cream: "#F5EFE0",
  card: "#FBF7F0",
  text: "#3A322D",
  veil: "#2B2721",
};

const LOGO_SRC = "/lovable-uploads/logo-sana-pilates.webp";
const FONTS_CSS =
  "https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,600;0,700;1,400&family=Nunito+Sans:wght@400;600;700&display=swap";

let fontsPromise: Promise<void> | null = null;

/** Carga la hoja de Fraunces y Nunito Sans una sola vez; resuelve cuando la hoja está lista. */
export function ensureBrandFonts(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  if (fontsPromise) return fontsPromise;
  fontsPromise = new Promise((resolve) => {
    const link = document.createElement("link");
    link.id = "sana-brand-fonts";
    link.rel = "stylesheet";
    link.href = FONTS_CSS;
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });
  return fontsPromise;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export interface CardOptions {
  /** Nombre de pila; si es vacío o null no se dibuja. */
  name?: string | null;
  /** Frase debajo de "completado"; por defecto la del hito. */
  copy?: string;
}

const SPRIG_PATHS = [
  "M200 0 C 170 80, 120 150, 60 290",
  "M180 60 q -30 4 -44 26 q 30 -2 44 -26z",
  "M160 105 q 28 6 40 30 q -28 -4 -40 -30z",
  "M138 150 q -30 2 -46 24 q 30 0 46 -24z",
  "M116 195 q 26 8 36 32 q -26 -6 -36 -32z",
];

function drawSprig(ctx: CanvasRenderingContext2D, x: number, y: number, rotate180: boolean) {
  ctx.save();
  ctx.translate(x, y);
  if (rotate180) {
    ctx.translate(220, 300);
    ctx.rotate(Math.PI);
  }
  ctx.globalAlpha = 0.35;
  ctx.strokeStyle = C.coral;
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  SPRIG_PATHS.forEach((d) => ctx.stroke(new Path2D(d)));
  ctx.restore();
}

function drawLotus(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(s, s);
  ctx.strokeStyle = "#EBA0A5";
  ctx.lineWidth = 3 / s;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const petals = [
    "M0 0 C -14 -18, -10 -40, 0 -52 C 10 -40, 14 -18, 0 0z",
    "M0 0 C -20 -8, -34 -24, -34 -40 C -18 -36, -6 -22, 0 0z",
    "M0 0 C 20 -8, 34 -24, 34 -40 C 18 -36, 6 -22, 0 0z",
    "M-2 0 C -22 2, -40 -6, -48 -18 C -30 -18, -14 -12, -2 0z",
    "M2 0 C 22 2, 40 -6, 48 -18 C 30 -18, 14 -12, 2 0z",
    "M-40 8 Q 0 18, 40 8",
  ];
  petals.forEach((d) => ctx.stroke(new Path2D(d)));
  ctx.restore();
}

export async function renderAchievementCard(milestone: number, stats: AchievementStats, opts: CardOptions = {}): Promise<Blob> {
  await ensureBrandFonts();
  await Promise.all([
    document.fonts.load('700 220px "Fraunces"'),
    document.fonts.load('400 60px "Fraunces"'),
    document.fonts.load('700 60px "Nunito Sans"'),
    document.fonts.load('400 40px "Nunito Sans"'),
    document.fonts.load('600 50px "Fraunces"'),
    document.fonts.load('italic 400 110px "Fraunces"'),
    document.fonts.load('600 40px "Nunito Sans"'),
  ]).catch(() => undefined);
  await document.fonts.ready;

  const W = 1080;
  const H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // Fondo crema con degradé radial suave
  ctx.fillStyle = C.cream;
  ctx.fillRect(0, 0, W, H);
  const g = ctx.createRadialGradient(W / 2, H * 0.4, 80, W / 2, H * 0.4, H * 0.75);
  g.addColorStop(0, C.card);
  g.addColorStop(0.6, C.cream);
  g.addColorStop(1, "#F2E5D8");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Decoración: ramitas en esquinas y lotos en los márgenes
  drawSprig(ctx, W - 210, -30, false);
  drawSprig(ctx, -70, 1650, true);
  drawLotus(ctx, 72, 660, 1);
  drawLotus(ctx, 1010, 930, 0.9);
  drawLotus(ctx, 1010, 1640, 0.9);

  // Logo
  const logo = await loadImage(LOGO_SRC);
  if (logo) {
    const lh = 300;
    const lw = (logo.width / logo.height) * lh;
    ctx.drawImage(logo, (W - lw) / 2, 110, lw, lh);
  }

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  const name = opts.name?.trim();
  if (name) {
    ctx.fillStyle = C.text;
    ctx.font = '700 44px "Nunito Sans", Arial, sans-serif';
    ctx.fillText(`¡Bien, ${name}!`, W / 2, 500, W - 240);
  }

  // Día grande
  ctx.fillStyle = C.text;
  ctx.font = '700 230px "Fraunces", Georgia, serif';
  ctx.fillText(`Día ${milestone}`, W / 2, 720);

  ctx.fillStyle = C.coral;
  ctx.font = 'italic 400 110px "Fraunces", Georgia, serif';
  ctx.fillText("completado", W / 2, 860);

  ctx.fillStyle = C.text;
  ctx.font = '400 46px "Nunito Sans", Arial, sans-serif';
  ctx.fillText(opts.copy ?? MILESTONE_COPY[milestone as Milestone] ?? "", W / 2, 960, W - 240);

  // Fila de 3 datos
  const boxY = 1070;
  const boxH = 260;
  const gap = 30;
  const boxW = (W - 120 * 2 - gap * 2) / 3;
  const data = [
    { v: String(stats.completedDays.length), l: "días" },
    { v: String(stats.totalMinutes), l: "minutos" },
    { v: String(stats.streak), l: "de racha" },
  ];
  data.forEach((d, i) => {
    const x = 120 + i * (boxW + gap);
    roundRect(ctx, x, boxY, boxW, boxH, 36);
    ctx.fillStyle = C.card;
    ctx.fill();
    ctx.strokeStyle = "rgba(58,50,45,0.12)";
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = C.coral;
    ctx.font = '700 104px "Fraunces", Georgia, serif';
    ctx.fillText(d.v, x + boxW / 2, boxY + 150);
    ctx.fillStyle = C.text;
    ctx.font = '600 40px "Nunito Sans", Arial, sans-serif';
    ctx.fillText(d.l, x + boxW / 2, boxY + 215);
  });

  // Barra de 28 segmentos
  const barX = 120;
  const barW = W - 240;
  const segGap = 8;
  const segW = (barW - segGap * 27) / 28;
  const barY = 1420;
  for (let i = 1; i <= 28; i++) {
    const x = barX + (i - 1) * (segW + segGap);
    roundRect(ctx, x, barY, segW, 44, 8);
    ctx.fillStyle = stats.completedDays.includes(i) ? C.coral : "rgba(58,50,45,0.12)";
    ctx.fill();
  }
  ctx.fillStyle = C.text;
  ctx.font = '400 36px "Nunito Sans", Arial, sans-serif';
  ctx.fillText(`${stats.completedDays.length} de 28 días`, W / 2, barY + 110);

  // Pie
  ctx.fillStyle = C.veil;
  ctx.font = '600 50px "Fraunces", Georgia, serif';
  ctx.fillText("Reto de 28 días de Pilates Asiático", W / 2, 1740);
  ctx.fillStyle = C.coral;
  ctx.font = '700 44px "Nunito Sans", Arial, sans-serif';
  ctx.fillText("sanaspilates.com", W / 2, 1810);

  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("No se pudo crear la imagen"))), "image/png"),
  );
}

/** Devuelve "shared" si se abrió el menú de compartir, "downloaded" si se descargó. */
export async function shareAchievement(milestone: number, stats: AchievementStats, opts: CardOptions = {}): Promise<"shared" | "downloaded" | "cancelled"> {
  const blob = await renderAchievementCard(milestone, stats, opts);
  const filename = `sana-pilates-dia-${milestone}.png`;
  const file = new File([blob], filename, { type: "image/png" });

  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file] });
      return "shared";
    } catch (e) {
      if ((e as Error)?.name === "AbortError") return "cancelled";
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return "downloaded";
}
